import { useQuery } from "@tanstack/react-query";
import { AlertCircle, ArrowLeft, Code, FileText, Loader2, Network, TrendingUp } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getJobResults, getJobStatus } from "@/api/documentApi";
import ConfidenceBar from "@/features/processing-results/components/ConfidenceBar";
import ProgressBar from "@/features/processing-results/components/ProgressBar";
import { useTheme } from "@/hooks/useTheme";
import type {
	Entity,
	FormalExpression,
	ProcessingResultsResponse,
	ProcessingStatusResponse,
	Relationship,
} from "@/types";
import { getMockResults, getMockStatus } from "@/utils/mockResultsData";

const ProcessingResultsPage: React.FC = () => {
	useTheme();

	const { jobId } = useParams<{ jobId: string }>();
	const navigate = useNavigate();
	const [shouldPoll, setShouldPoll] = useState(true);
	const isMock = jobId === "mock";

	// Poll for job status (or use mock data)
	const { data: status, isLoading: statusLoading } = useQuery<ProcessingStatusResponse>({
		queryKey: ["jobStatus", jobId],
		queryFn: ({ signal }) => {
			if (isMock) {
				return Promise.resolve(getMockStatus());
			}
			if (!jobId) {
				throw new Error("Job ID is required");
			}
			return getJobStatus(jobId, { signal });
		},
		enabled: !!jobId && (shouldPoll || isMock),
		refetchInterval: (query) => {
			if (isMock) return false;
			const data = query.state.data;
			if (data?.status === "completed" || data?.status === "failed") {
				setShouldPoll(false);
				return false;
			}

			const attempt = query.state.dataUpdateCount;
			if (attempt < 10) return 2000;
			if (attempt < 30) return 4000;
			if (attempt < 60) return 8000;
			return 15000;
		},
		refetchIntervalInBackground: false,
	});

	// Fetch results when job is completed (or use mock data)
	const { data: results } = useQuery<ProcessingResultsResponse>({
		queryKey: ["jobResults", jobId],
		queryFn: ({ signal }) => {
			if (isMock) {
				return Promise.resolve(getMockResults());
			}
			if (!jobId) {
				throw new Error("Job ID is required");
			}
			return getJobResults(jobId, { signal });
		},
		enabled: !!jobId && (status?.status === "completed" || isMock),
	});

	const isLoading = statusLoading || (status?.status !== "completed" && status?.status !== "failed" && shouldPoll);
	const isError = status?.status === "failed";
	const isCompleted = status?.status === "completed" && results;

	const handleBack = () => {
		navigate("/upload");
	};

	if (!jobId) {
		return (
			<div className="min-h-screen surface-alt flex items-center justify-center text-primary">
				<div className="text-center">
					<AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
					<h2 className="text-xl font-semibold text-primary mb-2">Job ID Missing</h2>
					<p className="text-muted mb-4">No job ID provided in the URL.</p>
					<div className="space-x-4">
						<button type="button" onClick={handleBack} className="btn btn-secondary">
							Go Back
						</button>
						<button type="button" onClick={() => navigate("/results/mock")} className="btn btn-primary">
							View Mock Example
						</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen surface-alt text-primary">
			{/* Header */}
			<header className="surface border-b border-subtle px-6 py-4">
				<div className="flex items-center justify-between">
					<div className="flex items-center space-x-4">
						<button
							type="button"
							onClick={handleBack}
							className="p-2 soft-hover rounded-lg transition-colors"
							aria-label="Go back"
						>
							<ArrowLeft className="w-5 h-5 text-muted" />
						</button>
						<div>
							<h1 className="text-xl font-semibold text-primary">
								Processing Results
								{isMock && (
									<span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">
										MOCK DATA
									</span>
								)}
							</h1>
							<p className="text-sm text-subtle">{isMock ? "Example Results (Mock Data)" : `Job ID: ${jobId}`}</p>
						</div>
					</div>
				</div>
			</header>

			<main className="max-w-7xl mx-auto px-6 py-8">
				{/* Loading State */}
				{isLoading && (
					<div className="surface rounded-lg shadow-sm border border-subtle p-12">
						<div className="text-center">
							<Loader2 className="w-12 h-12 text-primary-600 animate-spin mx-auto mb-4" />
							<h2 className="text-lg font-semibold text-primary mb-2">Processing Document...</h2>
							<p className="text-muted mb-4">
								{status?.currentStep || status?.step || status?.status || "Initializing..."}
							</p>
							{status?.progress !== undefined && (
								<div className="w-full max-w-md mx-auto">
									<ProgressBar value={status.progress} className="mb-2" />
									<p className="text-sm text-subtle">{status.progress}% complete</p>
								</div>
							)}
						</div>
					</div>
				)}

				{/* Error State */}
				{isError && (
					<div className="surface rounded-lg shadow-sm border border-red-200 p-8">
						<div className="flex items-start space-x-3">
							<AlertCircle className="w-6 h-6 text-red-600 mt-0.5" />
							<div className="flex-1">
								<h2 className="text-lg font-semibold text-red-900 mb-2">Processing Failed</h2>
								<p className="text-red-700 mb-4">
									{status?.error || "An error occurred while processing the document."}
								</p>
								<button type="button" onClick={handleBack} className="btn btn-secondary">
									Go Back
								</button>
							</div>
						</div>
					</div>
				)}

				{/* Results */}
				{isCompleted && results && (
					<div className="space-y-6">
						{/* Summary Stats */}
						<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
							<div className="surface rounded-lg shadow-sm border border-subtle p-6">
								<div className="flex items-center space-x-3 mb-2">
									<FileText className="w-6 h-6 text-primary-600" />
									<h3 className="text-lg font-semibold text-primary">Entities</h3>
								</div>
								<p className="text-3xl font-bold text-primary">{results.results.entities.length}</p>
								<p className="text-sm text-subtle">Extracted from document</p>
							</div>
							<div className="surface rounded-lg shadow-sm border border-subtle p-6">
								<div className="flex items-center space-x-3 mb-2">
									<Network className="w-6 h-6 text-green-600" />
									<h3 className="text-lg font-semibold text-primary">Relationships</h3>
								</div>
								<p className="text-3xl font-bold text-primary">{results.results.relationships.length}</p>
								<p className="text-sm text-subtle">Connections identified</p>
							</div>
							<div className="surface rounded-lg shadow-sm border border-subtle p-6">
								<div className="flex items-center space-x-3 mb-2">
									<Code className="w-6 h-6 text-purple-600" />
									<h3 className="text-lg font-semibold text-primary">Formal Expressions</h3>
								</div>
								<p className="text-3xl font-bold text-primary">{results.results.formalExpressions.length}</p>
								<p className="text-sm text-subtle">HOL expressions generated</p>
							</div>
						</div>

						{/* Entities Section */}
						<div className="surface rounded-lg shadow-sm border border-subtle p-6">
							<div className="flex items-center justify-between mb-6">
								<h2 className="text-xl font-semibold text-primary flex items-center space-x-2">
									<FileText className="w-5 h-5" />
									<span>Entities ({results.results.entities.length})</span>
								</h2>
							</div>
							{results.results.entities.length > 0 ? (
								<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
									{results.results.entities.map((entity: Entity) => (
										<div
											key={entity.name}
											className="border border-subtle rounded-lg p-4 hover:border-primary-300 transition-colors"
										>
											<div className="flex items-start justify-between mb-2">
												<h3 className="font-semibold text-primary text-lg">{entity.name}</h3>
												<span className="px-2 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded">
													{entity.type}
												</span>
											</div>
											<div className="space-y-2">
												<div className="flex items-center justify-between text-sm">
													<span className="text-subtle">Confidence:</span>
													<ConfidenceBar confidence={entity.confidence} tone="primary" widthClassName="w-24" />
												</div>
												{entity.mentions && entity.mentions.length > 0 && (
													<div className="text-sm">
														<span className="text-subtle">Mentions: </span>
														<span className="text-muted font-medium">{entity.mentions.length}</span>
													</div>
												)}
											</div>
										</div>
									))}
								</div>
							) : (
								<p className="text-subtle text-center py-8">No entities extracted.</p>
							)}
						</div>

						{/* Relationships Section */}
						{results.results.relationships.length > 0 && (
							<div className="surface rounded-lg shadow-sm border border-subtle p-6">
								<div className="flex items-center justify-between mb-6">
									<h2 className="text-xl font-semibold text-primary flex items-center space-x-2">
										<Network className="w-5 h-5" />
										<span>Relationships ({results.results.relationships.length})</span>
									</h2>
								</div>
								<div className="space-y-3">
									{results.results.relationships.map((rel: Relationship) => (
										<div
											key={`${rel.subject}-${rel.predicate}-${rel.object}`}
											className="border border-subtle rounded-lg p-4 hover:border-green-300 transition-colors"
										>
											<div className="flex items-center justify-between mb-3">
												<div className="flex items-center space-x-2">
													<span className="font-semibold text-primary">{rel.subject}</span>
													<TrendingUp className="w-4 h-4 text-green-600" />
													<span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
														{rel.predicate}
													</span>
													<TrendingUp className="w-4 h-4 text-green-600" />
													<span className="font-semibold text-primary">{rel.object}</span>
												</div>
												<ConfidenceBar confidence={rel.confidence} tone="green" widthClassName="w-20" />
											</div>
										</div>
									))}
								</div>
							</div>
						)}

						{/* Formal Expressions Section */}
						{results.results.formalExpressions.length > 0 && (
							<div className="surface rounded-lg shadow-sm border border-subtle p-6">
								<div className="flex items-center justify-between mb-6">
									<h2 className="text-xl font-semibold text-primary flex items-center space-x-2">
										<Code className="w-5 h-5" />
										<span>Formal Expressions ({results.results.formalExpressions.length})</span>
									</h2>
								</div>
								<div className="space-y-4">
									{results.results.formalExpressions.map((expr: FormalExpression) => (
										<div
											key={expr.expression}
											className="border border-subtle rounded-lg p-4 hover:border-purple-300 transition-colors"
										>
											<div className="flex items-start justify-between mb-3">
												<div className="flex-1">
													<code className="text-sm font-mono text-primary surface-alt px-3 py-2 rounded block">
														{expr.expression}
													</code>
												</div>
												<div className="ml-4">
													<ConfidenceBar confidence={expr.confidence} tone="primary" widthClassName="w-20" />
												</div>
											</div>
											<div className="flex items-center space-x-4 text-sm text-muted">
												<span>
													<span className="font-medium">Type:</span> {expr.type}
												</span>
												{expr.variables.length > 0 && (
													<span>
														<span className="font-medium">Variables:</span> {expr.variables.join(", ")}
													</span>
												)}
											</div>
										</div>
									))}
								</div>
							</div>
						)}
					</div>
				)}
			</main>
		</div>
	);
};

export default ProcessingResultsPage;
